import React, { Fragment as _ } from 'react';
import { css } from '@emotion/core';
import Html from 'slate-html-serializer';

const rules = [
  {
    deserialize(el, next) {
      const type = el && el.getAttribute && el.getAttribute('data-type');

      if (!type) return;

      if (
        [
          'code',
          'paragraph',
          'list-item',
          'bulleted-list',
          'numbered-list',
          'indented-list',
          'indented',
          'textAlignLeft',
          'textAlignCenter',
          'textAlignRight',
          'heading-one',
          'heading-two',
          'heading-three',
          'heading-four',
          'heading-five',
          'heading-six',
          'br',
          'span',
        ].includes(type)
      )
        return {
          object: 'block',
          type: type,
          data: {
            className: el.getAttribute('class'),
            element: el,
          },
          nodes: next(el.childNodes),
        };
    },
    serialize(obj, children) {
      if (obj.object == 'block') {
        switch (obj.type) {
          case 'paragraph':
            return (
              <p className={obj.data.get('className')} data-type={obj.type}>
                {children}
              </p>
            );

          case 'list-item':
            return (
              <li className="chameleon-li" data-type={obj.type}>
                {children}
              </li>
            );

          case 'bulleted-list':
            return (
              <ul className="chameleon-ul" data-type={obj.type}>
                {children}
              </ul>
            );

          case 'numbered-list':
            return (
              <ol className="chameleon-ol" data-type={obj.type}>
                {children}
              </ol>
            );

          case 'indented-list':
            return (
              <ul className="indented-list" data-type={obj.type}>
                {children}
              </ul>
            );

          case 'indented':
            return (
              <div
                className="indented"
                style={{ paddingLeft: 20 }}
                data-type={obj.type}
              >
                {children}
              </div>
            );

          case 'textAlignLeft':
            return (
              <div className="chameleon-text-align-left" data-type={obj.type}>
                {children}
              </div>
            );

          case 'textAlignCenter':
            return (
              <div className="chameleon-text-align-center" data-type={obj.type}>
                {children}
              </div>
            );

          case 'textAlignRight':
            return (
              <div className="chameleon-text-align-right" data-type={obj.type}>
                {children}
              </div>
            );

          case 'heading-one':
            return <h1 data-type={obj.type}>{children}</h1>;

          case 'heading-two':
            return <h2 data-type={obj.type}>{children}</h2>;

          case 'heading-three':
            return <h3 data-type={obj.type}>{children}</h3>;

          case 'heading-four':
            return <h4 data-type={obj.type}>{children}</h4>;

          case 'heading-five':
            return <h5 data-type={obj.type}>{children}</h5>;

          case 'heading-six':
            return <h6 data-type={obj.type}>{children}</h6>;

          case 'code':
            return (
              <pre className={'prettyprint'} data-type={obj.type}>
                <code>{children}</code>
              </pre>
            );

          case 'br':
            return <div data-type="br">&#65279;</div>;

          case 'span':
            return <span data-type={obj.type}>{children}</span>;

          case '':
            return <_>{children}</_>;
        }
      }
    },
  },
  {
    deserialize(el, next) {
      const type = el && el.getAttribute && el.getAttribute('data-type');
      if (!type) return;

      if (['link'].includes(type))
        return {
          object: 'inline',
          type: type,
          data: {
            className: el.getAttribute('class'),
            href: el.getAttribute('href'),
            title: el.getAttribute('title'),
            target: el.getAttribute('target'),
          },
          nodes: next(el.childNodes),
        };
    },
    serialize(obj, children) {
      const isInternalLink = false;

      if (obj.object == 'inline') {
        switch (obj.type) {
          case 'link': {
            const href = obj.data.get('href');

            return (
              <a
                href={href}
                title={obj.data.get('title')}
                target={obj.data.get('target')}
                style={
                  {
                    // color: 'inherit',
                    // cursor: 'pointer'
                  }
                }
                data-type={obj.type}
              >
                {children}
              </a>
            );
          }
        }
      }
    },
  },

  {
    deserialize(el, next) {
      const type = el && el.getAttribute && el.getAttribute('data-type');

      // !type ||
      if (
        ![
          'bold',
          'italic',
          'underline',
          'superscript',
          'subscript',
          'indent',
        ].includes(type)
      ) {
        return;
      }

      let style = el.getAttribute('style');
      if (style) {
        style = style.split(/:/g);
        style = {
          [style[0]]: style[1],
        };
      } else {
        style = {};
      }

      return {
        object: 'mark',
        type: type,
        nodes: next(el.childNodes),
      };
    },
    serialize(obj, children) {
      if (obj.object === 'mark') {
        switch (obj.type) {
          case 'bold':
            return <strong data-type={obj.type}>{children}</strong>;

          case 'italic':
            return <em data-type={obj.type}>{children}</em>;

          case 'underline':
            return <u data-type={obj.type}>{children}</u>;

          case 'superscript':
            return <sup data-type={obj.type}>{children}</sup>;

          case 'subscript':
            return <sub data-type={obj.type}>{children}</sub>;

          case 'indent':
            return <span data-type={obj.type}>{children}</span>;
        }
      }
    },
  },

  {
    deserialize(el, next) {
      const type = el && el.getAttribute && el.getAttribute('data-type');

      // !type ||
      if (
        !['fontFamily', 'fontSize', 'color', 'lang', 'variable'].includes(type)
      ) {
        return;
      }

      let style = el.getAttribute('style');
      if (style) {
        style = style.split(/:/g);
        style = {
          [style[0]]: style[1],
        };
      } else {
        style = {};
      }

      let fontSize = style['font-size'];

      if (fontSize && fontSize.match('rem')) {
        let fontSizeNumber = Number(
          style['font-size'].replace(/px/g, '').replace(/rem/g, '')
        );

        if (!isNaN(fontSizeNumber)) {
          fontSize = `${fontSizeNumber * 16}px`;
        }
      }

      return {
        object: 'mark',
        type: type,
        data: {
          className: el.getAttribute('class'),
          fontFamily: style['font-family'],
          fontSize,
          color: style['color'],
          lang: el.getAttribute('lang'),
          varName: el.getAttribute('data-varname'),
        },
        nodes: next(el.childNodes),
      };
    },
    serialize(obj, children) {
      const { data } = obj.toJS();

      if (obj.object === 'mark') {
        switch (obj.type) {
          case 'fontFamily':
            const { fontFamily } = data;
            return (
              <span style={{ fontFamily }} data-type={obj.type}>
                {children}
              </span>
            );
          case 'fontSize':
            const fontSizePXToREM = `${Number(
              String(data.fontSize).replace(/px/g, '')
            ) / 16}rem`;

            return (
              <span
                style={{
                  fontSize: fontSizePXToREM,
                }}
                data-type={obj.type}
              >
                {children}
              </span>
            );
          case 'color':
            return (
              <span
                css={css({
                  span: {
                    color: `${data.color} !important`,
                  },
                })}
                style={{ color: data.color }}
                data-type={obj.type}
              >
                {children}
              </span>
            );
          case 'lang':
            return (
              <span lang={data.lang} data-type={data.lang && obj.type}>
                {children}
              </span>
            );
          case 'variable': {
            // the nuclear backUp option if varName fails. . .
            let varNameFromChildren;
            try {
              varNameFromChildren = children?.toString();
            } catch (err) {
              console.warn('COULD NOT CONVERT CHILDREN TO VARNAME', err);
            }

            const varName = data.varName || varNameFromChildren;

            return (
              <span data-type="variable" data-varname={varName}>
                {children}
              </span>
            );
          }
        }
      }
    },
  },
];

//
const html = new Html({ rules });

export default html;
